// branch components
import BranchGasStep from '../../components/BranchGasStep/BranchGasStep';

// branch events
import branchSelectedEvent from '../../events/selected.event.branch';

// event HOCs
import subscriptionHOC from '@drivesally/sally-fw/lib/event/hoc/subscription.hoc.event';

// gas sections
import sections from '@drivesally/sally-fn/lib/gas/sections/sections';

// notify
import notify from '@drivesally/sally-ui/lib/libs/notify';

// propTypes
import PropTypes from 'prop-types';

// react
import React, {Component} from 'react';

// Each gas_config[type] entry is either a plain section key string (no
// flags) or {key, category, carry_over}. Internally we always work with the
// normalized object shape, and only re-serialize back down to a plain
// string on save when neither flag is set - keeping unflagged branches'
// JSON exactly as minimal as it was before this editor supported flags.
const normalizeEntry = (entry) =>
  typeof entry === 'object' && entry !== null
    ? {
        key: entry.key,
        category: entry.category || null,
        carry_over: !!entry.carry_over,
      }
    : {key: entry, category: null, carry_over: false};

// Sections that are no longer configurable going forward - not selectable in
// this editor, and dropped from a branch's config on next save if still
// present from before. Old Gas objects that already reference one of these
// stay viewable (see includeLegacySections in sections.gas.js) regardless of
// whether the branch's current config still lists it.
const EXCLUDED_SECTION_KEYS = ['checkout', 'launch', 'offboard', 'pictures', 'optionalPictures'];

const normalizeTypeConfig = (typeConfig) =>
  (!!typeConfig
    ? Array.isArray(typeConfig)
      ? typeConfig
      : [typeConfig]
    : []
  )
    .map(normalizeEntry)
    .filter(({key}) => !EXCLUDED_SECTION_KEYS.includes(key));

const serializeEntry = ({key, category, carry_over}) =>
  category || carry_over ? {key, category, carry_over} : key;

// Several sections share a display label with a "short"/simplified sibling
// that checks fewer fields - disambiguate them here, in this editor only,
// since the live app's own section buttons are unaffected by this and staff
// already know them by their plain label.
const SECTION_LABEL_SUFFIX = {
  tires: ' (Full)',
  short_tires: ' (Short)',
  lights: ' (Full)',
  short_lights: ' (Short)',
  cleaning: ' (Full)',
  short_cleaning: ' (Short)',
  dashcam: ' (Full)',
  short_dashcam: ' (Short)',
  pictures: ' (Full)',
  optionalPictures: ' (Short)',
};

class BranchGasStepContainer extends Component {
  static propTypes = {
    branch: PropTypes.object,
    Container: PropTypes.any,
    goingBack: PropTypes.bool,
    onBack: PropTypes.func,
    onClose: PropTypes.func,
    onNext: PropTypes.func,
    setStepChangeValidator: PropTypes.func,
    subscribe: PropTypes.func,
    visible: PropTypes.bool,
  };

  state = {
    offboard: [],
    onboard: [],
    onsite_sitting: [],
    onsite: [],
  };

  changedAttributes = [];

  componentDidMount() {
    const {setStepChangeValidator, subscribe} = this.props;
    this.initialize();
    subscribe(branchSelectedEvent.subscribe(this.branchSelected));
    setStepChangeValidator(this.stepChangeValidator);
  }

  initialize = (branch = this.props.branch) => {
    this.setState({
      offboard: normalizeTypeConfig(branch?.gas_config?.offboard),
      onboard: normalizeTypeConfig(branch?.gas_config?.onboard),
      onsite_sitting: normalizeTypeConfig(branch?.gas_config?.onsite_sitting),
      onsite: normalizeTypeConfig(branch?.gas_config?.onsite),
    });
  };

  copySettings = (branch) => {
    this.changedAttributes = ['gas_config'];
    this.initialize(branch);
  };

  branchSelected = ({branch}) => {
    notify({
      id: 'changeBranch',
      title: 'Confirm',
      icon: undefined,
      content:
        'Are you sure you want to copy settings a different branch? This will undo this whole section.',
      primary: {
        label: 'Cancel',
        onClick: () => null,
      },
      secondary: {
        label: 'Continue',
        onClick: () => this.copySettings(branch),
      },
    });
  };

  generateBranch = () => {
    const {branch} = this.props;
    const {offboard, onboard, onsite_sitting, onsite} = this.state;
    return {
      ...branch,
      gas_config: {
        offboard: offboard.map(serializeEntry),
        onboard: onboard.map(serializeEntry),
        onsite_sitting: onsite_sitting.map(serializeEntry),
        onsite: onsite.map(serializeEntry),
      },
    };
  };

  continue = () => {
    const {onNext} = this.props;
    const branch = this.generateBranch();
    onNext({branch, changedAttributes: this.changedAttributes});
  };

  back = async () => {
    const {onBack} = this.props;
    const branch = this.generateBranch();
    onBack({branch, changedAttributes: this.changedAttributes});
  };

  stepChangeValidator = async () => {
    const branch = await this.generateBranch();
    return {
      shouldContinue: true,
      data: {branch, changedAttributes: this.changedAttributes},
    };
  };

  change = ({type, section}) => () => {
    this.changedAttributes = ['gas_config'];
    const exists = this.state[type].some((entry) => entry.key === section);
    const settings = exists
      ? this.state[type].filter((entry) => entry.key !== section)
      : [...this.state[type], normalizeEntry(section)];
    this.setState({[type]: settings});
  };

  changeCategory = ({type, section}) => (category) => {
    this.changedAttributes = ['gas_config'];
    this.setState({
      [type]: this.state[type].map((entry) =>
        entry.key === section ? {...entry, category: category || null} : entry
      ),
    });
  };

  changeCarryOver = ({type, section}) => (carry_over) => {
    this.changedAttributes = ['gas_config'];
    this.setState({
      [type]: this.state[type].map((entry) =>
        entry.key === section ? {...entry, carry_over} : entry
      ),
    });
  };

  sections = () => {
    const preparedSections = [...sections]
      .filter(({key}) => !EXCLUDED_SECTION_KEYS.includes(key))
      .map(({key, label, icon}) => ({
        key,
        label: `${label}${SECTION_LABEL_SUFFIX[key] || ''}`,
        icon,
      }));
    return [
      {
        type: 'onboard',
        label: 'Onboard (Launching)',
        sections: [...preparedSections],
      },
      {
        type: 'offboard',
        label: 'Offboard (Returning)',
        sections: [...preparedSections],
      },
      {
        type: 'onsite_sitting',
        label: 'Onsite',
        sections: [...preparedSections],
      },
    ];
  };

  render() {
    const {Container} = this.props;
    const {...attributes} = this.state;
    return (
      <BranchGasStep
        {...attributes}
        Container={Container}
        onBack={this.back}
        onChange={this.change}
        onChangeCategory={this.changeCategory}
        onChangeCarryOver={this.changeCarryOver}
        onNext={this.continue}
        sections={this.sections()}
      />
    );
  }
}

export default subscriptionHOC(BranchGasStepContainer);
